import React, { FC, useState, useRef, useEffect } from "react";
import { useCookies } from "react-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { authenticatedFetch, AUTH_COOKIE_MAX_AGE_MS } from "../../utils/api";
import { resolveImageUrl, API_BASE } from "../../config/api";
import { MIN_PASSWORD_LENGTH, isValidNewPassword } from "../../utils/passwordValidation";
import style from "./style.module.css";

const ROLE_LABELS: Record<string, string> = { O: "Owner", A: "Admin", S: "Sales", C: "Customer" };
const ROLE_COLORS: Record<string, string> = { O: "#7c3aed", A: "#0369a1", S: "#0f766e", C: "#6b7280" };

const MOBILE_REGEX = /^\+92[0-9]{10}$/;
const validateMobile = (val: string) => {
  if (!val) return null;
  if (!MOBILE_REGEX.test(val)) return "Must start with +92 followed by exactly 10 digits (e.g. +923001234567)";
  return null;
};

// Same helper used by StorePage — returns raw base64 without the data: prefix
const convertFileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]); // raw base64, no prefix
    };
    reader.onerror = reject;
  });

const ProfilePage: FC = () => {
  const [cookies, setCookie] = useCookies(["auth"]);
  const auth = cookies.auth || {};

  const [form, setForm] = useState({
    first_name: auth.first_name || "",
    last_name: auth.last_name || "",
    email: auth.email || "",
    mobile: auth.mobile || "",
  });

  // imageFile holds the selected File; preview is only for local display
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(
    auth.profile_image ? resolveImageUrl(auth.profile_image) : null
  );

  const [mobileError, setMobileError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [pwdSection, setPwdSection] = useState(false);
  // step: "idle" | "sending" | "code_sent" | "saving"
  const [pwdStep, setPwdStep] = useState<"idle" | "sending" | "code_sent" | "saving">("idle");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState({ new: "", confirm: "" });
  const [showPwd, setShowPwd] = useState({ new: false, confirm: false });
  const [pwdMsg, setPwdMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  // Sync preview with the cookie when there is no pending local file selected
  useEffect(() => {
    if (!imageFile) {
      setPreview(auth.profile_image ? resolveImageUrl(auth.profile_image) : null);
    }
  }, [auth.profile_image]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleLabel = ROLE_LABELS[auth.user_type] || auth.user_type || "";
  const roleColor = ROLE_COLORS[auth.user_type] || "#6b7280";
  const initials = [auth.first_name?.[0], auth.last_name?.[0]].filter(Boolean).join("").toUpperCase() || "U";
  const fullName = [form.first_name, form.last_name].filter(Boolean).join(" ") || "User";

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    // Show local preview immediately (data URL)
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    // Only validate mobile if the user changed it from the stored value
    const originalMobile = auth.mobile || "";
    const mobileErr = form.mobile !== originalMobile ? validateMobile(form.mobile) : null;
    if (mobileErr) { setMobileError(mobileErr); return; }

    setSaving(true);
    setMsg(null);
    try {
      // Convert image to raw base64 at save time — same as StorePage
      let imageBase64: string | null = null;
      if (imageFile) {
        try {
          imageBase64 = await convertFileToBase64(imageFile);
        } catch (err) {
          console.error("Error converting image:", err);
          setMsg({ type: "error", text: "Failed to process the selected image." });
          return;
        }
      }

      const payload: any = { ...form };
      if (imageBase64) payload.profile_image = imageBase64;

      const res = await authenticatedFetch(`${API_BASE}/user/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }, auth);

      const data = await res.json();
      if (data.success) {
        // Update the cookie so the sidebar and other pages reflect the new data
        const savedImagePath = data.user.profile_image_url || data.user.profile_image;
        const updatedAuth = {
          ...auth,
          first_name: data.user.first_name,
          last_name: data.user.last_name,
          email: data.user.email,
          mobile: data.user.mobile ?? auth.mobile,
          profile_image: savedImagePath || auth.profile_image,
        };
        setCookie("auth", updatedAuth, { path: "/", maxAge: AUTH_COOKIE_MAX_AGE_MS / 1000 });

        // Switch preview from local blob → server URL
        if (savedImagePath) setPreview(resolveImageUrl(savedImagePath));
        setImageFile(null);
        if (fileRef.current) fileRef.current.value = "";

        setMsg({ type: "success", text: "Profile updated successfully!" });
      } else {
        setMsg({ type: "error", text: data.message || "Failed to update profile." });
      }
    } catch {
      setMsg({ type: "error", text: "Network error. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleSendCode = async () => {
    // Validate passwords before sending the code
    if (!isValidNewPassword(pwd.new)) { setPwdMsg({ type: "error", text: `New password must be at least ${MIN_PASSWORD_LENGTH} characters.` }); return; }
    if (pwd.new !== pwd.confirm) { setPwdMsg({ type: "error", text: "Passwords do not match." }); return; }

    setPwdStep("sending");
    setPwdMsg(null);
    try {
      const res = await authenticatedFetch(`${API_BASE}/user/send-password-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }, auth);

      let data: any;
      try {
        data = await res.json();
      } catch {
        setPwdStep("idle");
        setPwdMsg({ type: "error", text: `Server error (${res.status}). Restart the backend server and try again.` });
        return;
      }

      if (data.success) {
        setPwdStep("code_sent");
        setPwdMsg({ type: "success", text: `Code sent to ${auth.email}. Enter it below.` });
      } else {
        setPwdStep("idle");
        setPwdMsg({ type: "error", text: data.message || "Failed to send code." });
      }
    } catch {
      setPwdStep("idle");
      setPwdMsg({ type: "error", text: "Cannot reach the server. Make sure the backend is running." });
    }
  };

  const handlePasswordSave = async () => {
    if (!code.trim()) { setPwdMsg({ type: "error", text: "Enter the verification code from your email." }); return; }

    setPwdStep("saving");
    setPwdMsg(null);
    try {
      const res = await authenticatedFetch(`${API_BASE}/user/reset-password-with-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim(), new_password: pwd.new }),
      }, auth);
      const data = await res.json();
      if (data.success) {
        setPwdMsg({ type: "success", text: "Password changed successfully!" });
        setPwd({ new: "", confirm: "" });
        setCode("");
        setPwdStep("idle");
      } else {
        setPwdStep("code_sent");
        setPwdMsg({ type: "error", text: data.message || "Failed to change password." });
      }
    } catch {
      setPwdStep("code_sent");
      setPwdMsg({ type: "error", text: "Network error. Please try again." });
    }
  };

  const handleResetPwdFlow = () => {
    setPwdStep("idle");
    setPwd({ new: "", confirm: "" });
    setCode("");
    setPwdMsg(null);
  };

  return (
    <div className={style.wrapper}>
      <div className={style.pageHeader}>
        <h1 className={style.title}>My Profile</h1>
        <p className={style.subtitle}>Manage your personal information and account settings</p>
      </div>

      <div className={style.layout}>
        {/* Left card — Avatar + role info */}
        <div className={style.avatarCard}>
          <div className={style.avatarWrap}>
            {preview ? (
              <img
                src={preview}
                alt={fullName}
                className={style.avatarImg}
                onError={() => setPreview(null)}
              />
            ) : (
              <span className={style.avatarInitials} style={{ background: roleColor }}>{initials}</span>
            )}
            <button className={style.avatarEditBtn} onClick={() => fileRef.current?.click()} title="Change photo">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <input
              type="file"
              ref={fileRef}
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
          </div>
          <h2 className={style.avatarName}>{fullName}</h2>
          <span className={style.roleBadge} style={{ background: roleColor }}>{roleLabel}</span>
          {imageFile && (
            <span className={style.pendingBadge}>Image selected — click Save Changes</span>
          )}
          {auth.email && <p className={style.avatarEmail}>{auth.email}</p>}
        </div>

        {/* Right col — forms */}
        <div className={style.formsCol}>
          {/* Profile info card */}
          <div className={style.card}>
            <div className={style.cardHeader}>
              <span className={style.cardIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              Personal Information
            </div>
            <div className={style.cardBody}>
              {msg && (
                <div className={`${style.alert} ${msg.type === "success" ? style.alertSuccess : style.alertError}`}>
                  {msg.text}
                </div>
              )}
              <div className={style.formGrid}>
                <div className={style.formGroup}>
                  <label className={style.label}>First Name</label>
                  <input
                    className={style.input}
                    value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="First name"
                  />
                </div>
                <div className={style.formGroup}>
                  <label className={style.label}>Last Name</label>
                  <input
                    className={style.input}
                    value={form.last_name}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Last name"
                  />
                </div>
                <div className={style.formGroup}>
                  <label className={style.label}>Email</label>
                  <input
                    className={style.input}
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
                <div className={style.formGroup}>
                  <label className={style.label}>Mobile</label>
                  <input
                    className={`${style.input} ${mobileError ? style.inputError : ""}`}
                    value={form.mobile}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(f => ({ ...f, mobile: val }));
                      setMobileError(validateMobile(val));
                    }}
                    placeholder="+923001234567"
                    maxLength={13}
                  />
                  {mobileError && <span className={style.fieldError}>{mobileError}</span>}
                </div>
                <div className={style.formGroup}>
                  <label className={style.label}>Role</label>
                  <input className={`${style.input} ${style.inputReadonly}`} value={roleLabel} readOnly />
                </div>
              </div>
              <div className={style.cardActions}>
                <button className={style.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>

          {/* Password card */}
          <div className={style.card}>
            <div className={style.cardHeader} style={{ cursor: "pointer" }} onClick={() => { setPwdSection(p => !p); handleResetPwdFlow(); }}>
              <span className={style.cardIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </span>
              Change Password
              <span className={style.cardToggle}>{pwdSection ? "▲" : "▼"}</span>
            </div>
            {pwdSection && (
              <div className={style.cardBody}>
                {pwdMsg && (
                  <div className={`${style.alert} ${pwdMsg.type === "success" ? style.alertSuccess : style.alertError}`}>
                    {pwdMsg.text}
                  </div>
                )}

                {/* Step 1: enter new password then send code */}
                {(pwdStep === "idle" || pwdStep === "sending") && (
                  <div className={style.formStack}>
                    <div className={style.formGroup}>
                      <label className={style.label}>New Password</label>
                      <div className={style.pwdWrap}>
                        <input
                          className={style.input}
                          type={showPwd.new ? "text" : "password"}
                          value={pwd.new}
                          onChange={e => setPwd(p => ({ ...p, new: e.target.value }))}
                          placeholder="Min. 6 characters"
                          disabled={pwdStep === "sending"}
                          autoComplete="new-password"
                        />
                        <button type="button" className={style.eyeBtn} onClick={() => setShowPwd(s => ({ ...s, new: !s.new }))}>
                          <FontAwesomeIcon icon={showPwd.new ? faEyeSlash : faEye} />
                        </button>
                      </div>
                    </div>
                    <div className={style.formGroup}>
                      <label className={style.label}>Confirm New Password</label>
                      <div className={style.pwdWrap}>
                        <input
                          className={style.input}
                          type={showPwd.confirm ? "text" : "password"}
                          value={pwd.confirm}
                          onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
                          placeholder="Repeat new password"
                          disabled={pwdStep === "sending"}
                          autoComplete="new-password"
                        />
                        <button type="button" className={style.eyeBtn} onClick={() => setShowPwd(s => ({ ...s, confirm: !s.confirm }))}>
                          <FontAwesomeIcon icon={showPwd.confirm ? faEyeSlash : faEye} />
                        </button>
                      </div>
                    </div>
                    <div className={style.cardActions}>
                      <button className={style.saveBtn} onClick={handleSendCode} disabled={pwdStep === "sending"}>
                        {pwdStep === "sending" ? "Sending Code…" : "Send Code"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: enter the code received by email */}
                {(pwdStep === "code_sent" || pwdStep === "saving") && (
                  <div className={style.formStack}>
                    <div className={style.formGroup}>
                      <label className={style.label}>Verification Code</label>
                      <input
                        className={style.input}
                        value={code}
                        onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="Enter the 6-digit code from your email"
                        inputMode="numeric"
                        maxLength={6}
                        autoFocus
                        disabled={pwdStep === "saving"}
                      />
                    </div>
                    <div className={style.cardActions} style={{ gap: "8px" }}>
                      <button className={style.saveBtn} onClick={handlePasswordSave} disabled={pwdStep === "saving"}>
                        {pwdStep === "saving" ? "Changing Password…" : "Change Password"}
                      </button>
                      <button
                        className={style.saveBtn}
                        style={{ background: "#6b7280" }}
                        onClick={handleResetPwdFlow}
                        disabled={pwdStep === "saving"}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
